import {
  Boxes,
  Camera,
  ChevronRight,
  Database,
  HardDrive,
  KeyRound,
  MemoryStick,
  Network,
  RefreshCw,
  Route,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { api } from "../api";
import {
  ActionButton,
  ErrorState,
  LoadingState,
  Panel,
  SandArcs,
  SectionHeader,
  StatusDot,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type { RouteKey, VpnStatus } from "../types";
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
} from "../utils";

const OVERVIEW_REFRESH_INTERVAL_MS = 30_000;

export function OverviewPage({
  onNavigate,
}: {
  onNavigate: (route: RouteKey) => void;
}) {
  const application = useResource(
    () => api.overviewApplication(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const database = useResource(
    () => api.overviewDatabase(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const dependencies = useResource(
    () => api.overviewDependencies(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const redis = useResource(
    () => api.overviewRedis(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const sessions = useResource(
    () => api.overviewSessions(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const storage = useResource(
    () => api.overviewStorage(),
    [],
    OVERVIEW_REFRESH_INTERVAL_MS,
  );
  const vpn = useResource(() => api.vpn(), [], OVERVIEW_REFRESH_INTERVAL_MS);
  const jobs = useResource(() => api.jobs(), [], OVERVIEW_REFRESH_INTERVAL_MS);
  const resources = [
    application,
    database,
    dependencies,
    redis,
    sessions,
    storage,
    vpn,
    jobs,
  ];
  const refreshAll = () => Promise.all(resources.map((item) => item.refresh()));
  const allDependenciesUp = dependencies.data
    ? Object.values(dependencies.data).every((item) => item.status === "up")
    : false;
  const stats = [
    {
      label: "注册用户",
      value: database.data ? formatNumber(database.data.users.total) : null,
      annotation: database.data
        ? `当前在线 ${formatNumber(database.data.users.online3m)} 位`
        : null,
      hint: database.data
        ? `24h活跃 ${formatNumber(database.data.users.active24h)} 位；周活跃 ${formatNumber(database.data.users.active7d)} 位`
        : "用户活跃统计",
      loading: database.loading && !database.data,
      error: database.error,
      icon: UserRound,
      route: "users" as RouteKey,
    },
    {
      label: "有效会话",
      value: sessions.data ? formatNumber(sessions.data.activeSessions) : null,
      annotation: null,
      hint: "Redis 中的滑动登录会话",
      loading: sessions.loading && !sessions.data,
      error: sessions.error,
      icon: KeyRound,
      route: "users" as RouteKey,
    },
    {
      label: "持久快照",
      value: database.data ? formatNumber(database.data.snapshots.total) : null,
      annotation: null,
      hint: database.data
        ? `${formatBytes(database.data.snapshots.payloadBytes)} 用户数据`
        : "用户数据快照",
      loading: database.loading && !database.data,
      error: database.error,
      icon: Camera,
      route: "data" as RouteKey,
    },
    {
      label: "MinIO 对象",
      value: storage.data ? formatNumber(storage.data.objectCount) : null,
      annotation: null,
      hint: storage.data
        ? `${formatBytes(storage.data.totalBytes)} 对象存储`
        : "对象存储统计",
      loading: storage.loading && !storage.data,
      error: storage.error,
      icon: Boxes,
      route: "data" as RouteKey,
    },
  ];

  const runtime = [
    [
      "后端版本",
      application.data
        ? `v${application.data.application.version}`
        : application.error
          ? "读取失败"
          : null,
    ],
    [
      "Node.js",
      application.data?.application.nodeVersion ||
        (application.error ? "读取失败" : null),
    ],
    [
      "运行时间",
      application.data
        ? formatDuration(application.data.application.uptimeSeconds)
        : application.error
          ? "读取失败"
          : null,
    ],
    [
      "进程内存",
      application.data
        ? formatBytes(application.data.application.memory.rssBytes)
        : application.error
          ? "读取失败"
          : null,
    ],
    [
      "Redis 键",
      redis.data
        ? formatNumber(redis.data.keys)
        : redis.error
          ? "读取失败"
          : null,
    ],
    [
      "今日审计",
      database.data
        ? formatNumber(database.data.audit.today)
        : database.error
          ? "读取失败"
          : null,
    ],
  ];

  return (
    <div className="relative space-y-10">
      <SandArcs className="absolute -top-10 right-0 z-0 w-[520px] max-w-full opacity-80" />

      <section className="relative z-[1]">
        <SectionHeader
          eyebrow="AT A GLANCE"
          title="今日概况"
          desc={
            application.data
              ? `数据截至 ${formatDateTime(application.data.generatedAt, true)} · 每 30 秒自动刷新`
              : "数据分项加载中 · 每 30 秒自动刷新"
          }
          className="rise-in"
          action={
            <ActionButton
              busy={resources.some((item) => item.refreshing)}
              onClick={() => void refreshAll()}
            >
              <RefreshCw size={13} /> 刷新
            </ActionButton>
          }
        />
        <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          {stats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <button
                key={stat.label}
                onClick={() => onNavigate(stat.route)}
                className={`rise-in rise-in-${index + 1} group relative overflow-hidden rounded-2xl border border-sand-light/80 bg-paper p-5 text-left shadow-[0_1px_2px_rgba(46,42,37,0.04)] transition-colors duration-300 hover:border-sand sm:p-6`}
              >
                <div className="relative z-[1] flex items-center justify-between">
                  <p className="text-[12.5px] font-medium text-ink-soft">
                    {stat.label}
                  </p>
                  <StatIcon
                    size={16}
                    strokeWidth={1.8}
                    className="text-ink-faint transition group-hover:text-clay"
                  />
                </div>
                <div className="relative z-[1] mt-4 flex min-w-0 flex-nowrap items-baseline gap-1.5">
                  {stat.loading ? (
                    <InlineSkeleton className="h-12 w-24" />
                  ) : (
                    <p className="flex-none font-serif-num text-[42px] leading-none tracking-tight text-ink sm:text-[48px]">
                      {stat.value ?? "—"}
                    </p>
                  )}
                  {stat.annotation ? (
                    <span className="flex-none whitespace-nowrap text-[clamp(8px,2vw,11px)] font-medium text-ink-faint">
                      （{stat.annotation}）
                    </span>
                  ) : null}
                </div>
                <p className="relative z-[1] mt-3 text-[12px] leading-relaxed text-ink-faint">
                  {stat.error ? "读取失败，请刷新" : stat.hint}
                </p>
                <span className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-peach transition-transform duration-500 group-hover:scale-110" />
              </button>
            );
          })}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-5">
        <Panel className="rise-in rise-in-2 overflow-hidden lg:col-span-3">
          <div className="flex items-center justify-between border-b border-sand-light/70 px-6 py-4">
            <div>
              <p className="eyebrow text-clay">DEPENDENCIES</p>
              <h3 className="mt-1 font-display text-[16px] font-medium">
                基础设施
              </h3>
            </div>
            <Tag
              tone={
                dependencies.data
                  ? allDependenciesUp
                    ? "sage"
                    : "danger"
                  : "neutral"
              }
            >
              {dependencies.data
                ? allDependenciesUp
                  ? "全部可用"
                  : "存在异常"
                : dependencies.error
                  ? "读取失败"
                  : "读取中"}
            </Tag>
          </div>
          <div className="divide-y divide-sand-light/60">
            {dependencies.loading && !dependencies.data ? (
              <LoadingState rows={3} />
            ) : dependencies.error && !dependencies.data ? (
              <ErrorState
                error={dependencies.error}
                onRetry={() => void dependencies.refresh()}
              />
            ) : null}
            {Object.values(dependencies.data || {}).map((dependency) => {
              const DependencyIcon =
                dependency.name === "mysql"
                  ? Database
                  : dependency.name === "redis"
                    ? MemoryStick
                    : HardDrive;
              return (
                <div
                  key={dependency.name}
                  className="group flex items-center gap-4 px-6 py-4 transition hover:bg-cream/60"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-peach text-clay-deep">
                    <DependencyIcon size={17} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium">
                      {dependencyName(dependency.name)}
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-faint">
                      服务连接与健康检查
                    </p>
                  </div>
                  <svg
                    width="56"
                    height="22"
                    viewBox="0 0 56 22"
                    className="hidden sm:block"
                    aria-hidden
                  >
                    <path
                      d="M0 16 L8 16 L11 8 L15 20 L19 4 L23 16 L32 16 L35 10 L39 18 L43 12 L47 16 L56 16"
                      stroke="#C15F3C"
                      strokeWidth="1.3"
                      fill="none"
                      opacity="0.6"
                    />
                  </svg>
                  <div className="text-right">
                    <p className="font-serif-num text-[16px] text-ink">
                      {dependency.latencyMs} ms
                    </p>
                    <p className="text-[11px] text-ink-faint">响应</p>
                  </div>
                  <StatusDot
                    tone={dependency.status === "up" ? "ok" : "down"}
                    label={dependency.status === "up" ? "正常" : "异常"}
                  />
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel className="rise-in rise-in-3 lg:col-span-2">
          <div className="border-b border-sand-light/70 px-6 py-4">
            <p className="eyebrow text-clay">RUNTIME</p>
            <h3 className="mt-1 font-display text-[16px] font-medium">
              应用进程
            </h3>
          </div>
          <dl className="px-6 py-2">
            {runtime.map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4 border-b border-dashed border-sand-light/70 py-2.5 last:border-0"
              >
                <dt className="text-[12.5px] text-ink-soft">{label}</dt>
                <dd className="text-right text-[13.5px] font-medium text-ink">
                  {value ?? <InlineSkeleton className="h-3 w-20" />}
                </dd>
              </div>
            ))}
          </dl>
        </Panel>
      </section>

      <section className="rise-in rise-in-4">
        <SectionHeader eyebrow="CAMPUS NETWORK" title="校园网络" />
        <Panel className="mt-5 overflow-hidden">
          {vpn.loading && !vpn.data ? (
            <LoadingState rows={4} />
          ) : vpn.error && !vpn.data ? (
            <ErrorState error={vpn.error} onRetry={() => void vpn.refresh()} />
          ) : vpn.data ? (
            <div className="grid divide-y divide-sand-light/60 md:grid-cols-3 md:divide-x md:divide-y-0">
              <VpnState
                icon={Network}
                title="Tailscale 主链路"
                status={vpnPrimaryLabel(vpn.data.primary.status)}
                tone={
                  vpn.data.primary.status === "up"
                    ? "sage"
                    : vpn.data.primary.status === "unknown"
                      ? "neutral"
                      : "danger"
                }
                detail={tailscaleDetail(vpn.data)}
              />
              <VpnState
                icon={Route}
                title="当前请求链路"
                status={
                  vpn.data.activeMode === "tailscale"
                    ? "Tailscale"
                    : vpn.data.activeMode === "legacy"
                      ? "aTrust"
                      : "不可用"
                }
                tone={vpn.data.activeMode === "none" ? "danger" : "sage"}
                detail={`故障切换 ${formatNumber(vpn.data.failover.count)} 次${
                  vpn.data.failover.lastAt
                    ? ` · 最近 ${formatDateTime(vpn.data.failover.lastAt, true)}`
                    : ""
                }`}
              />
              <VpnState
                icon={ShieldCheck}
                title="aTrust 热备链路"
                status={
                  vpn.data.fallback.reconnecting
                    ? "正在重连"
                    : vpn.data.fallback.connected
                      ? "已连接"
                      : vpn.data.fallback.lastError
                        ? "连接失败"
                        : "等待凭据"
                }
                tone={
                  vpn.data.fallback.connected
                    ? "sage"
                    : vpn.data.fallback.lastError
                      ? "danger"
                      : "neutral"
                }
                detail={atrustDetail(vpn.data)}
              />
            </div>
          ) : null}
          {vpn.data?.primary.lastError ? (
            <div className="border-t border-sand-light/60 bg-cream/50 px-6 py-3 text-[12px] leading-relaxed text-ink-soft">
              最近异常：{vpn.data.primary.lastError}
            </div>
          ) : null}
        </Panel>
      </section>

      <section className="rise-in rise-in-4">
        <SectionHeader
          eyebrow="SCHEDULED JOBS"
          title="定时任务"
          action={
            <button
              onClick={() => onNavigate("jobs")}
              className="hidden items-center gap-1 text-[12.5px] text-ink-faint transition hover:text-clay-deep sm:flex"
            >
              查看全部 <ChevronRight size={14} />
            </button>
          }
        />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {jobs.loading && !jobs.data ? (
            <Panel className="md:col-span-2">
              <LoadingState rows={4} />
            </Panel>
          ) : jobs.error && !jobs.data ? (
            <Panel className="md:col-span-2">
              <ErrorState
                error={jobs.error}
                onRetry={() => void jobs.refresh()}
              />
            </Panel>
          ) : null}
          {(jobs.data || []).slice(0, 2).map((job) => (
            <Panel
              key={job.name}
              className="group relative overflow-hidden p-6 transition hover:border-sand"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="eyebrow text-[10px] text-ink-faint">
                    {job.name}
                  </p>
                  <h4 className="mt-1.5 text-[15px] font-medium leading-snug">
                    {job.description}
                  </h4>
                </div>
                <Tag
                  tone={
                    job.status === "failed"
                      ? "danger"
                      : job.status === "success"
                        ? "sage"
                        : "neutral"
                  }
                >
                  {jobStatus(job.status)}
                </Tag>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-[12px] text-ink-soft">
                <code className="rounded-lg bg-cream-deep px-2.5 py-1 font-mono text-[11.5px] text-clay-deep">
                  {job.expression}
                </code>
                <span className="text-ink-faint">·</span>
                <span>{job.timezone}</span>
              </div>
              <span className="absolute -right-6 -top-6 h-20 w-20 rounded-full border border-sand opacity-0 transition-all duration-500 group-hover:opacity-100" />
            </Panel>
          ))}
          {jobs.data && !jobs.data.length ? (
            <Panel className="p-6 text-[13px] text-ink-faint">
              尚未注册定时任务。
            </Panel>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function VpnState({
  icon: Icon,
  title,
  status,
  tone,
  detail,
}: {
  icon: typeof Network;
  title: string;
  status: string;
  tone: "sage" | "danger" | "neutral";
  detail: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-4 px-6 py-5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-peach text-clay-deep">
        <Icon size={17} strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[14px] font-medium">{title}</h3>
          <Tag tone={tone}>{status}</Tag>
        </div>
        <p className="mt-1.5 text-[12px] leading-relaxed text-ink-faint">
          {detail}
        </p>
      </div>
    </div>
  );
}

function InlineSkeleton({ className }: { className: string }) {
  return (
    <span
      aria-label="正在加载"
      className={`loading-line inline-block max-w-full ${className}`}
    />
  );
}

function tailscaleDetail(vpn: VpnStatus) {
  if (vpn.primary.status === "unknown") return "尚未检测校内资源";
  if (vpn.activeMode === "legacy" && vpn.primary.recoverySuccesses > 0) {
    return `恢复确认 ${vpn.primary.recoverySuccesses}/${vpn.primary.recoverySuccessesRequired}`;
  }
  if (vpn.primary.campusReachable) {
    return `校内资源 ${vpn.primary.campusLatencyMs ?? "—"} ms`;
  }
  return vpn.primary.health === "up"
    ? "隧道在线，校内资源暂不可达"
    : "容器或隧道离线";
}

function atrustDetail(vpn: VpnStatus) {
  const fallback = vpn.fallback;
  if (fallback.reconnecting) {
    const attempt = fallback.reconnectAttempt
      ? `第 ${fallback.reconnectAttempt} 次重连`
      : "正在重新连接";
    return fallback.lastError ? `${attempt} · ${fallback.lastError}` : attempt;
  }
  if (fallback.latencyMs !== null) {
    const source =
      fallback.latencySource === "traffic" ? "实际请求" : "空闲探测";
    return `校内资源 ${fallback.latencyMs} ms · ${source}`;
  }
  if (fallback.connected) return "已连接，等待延迟样本";
  return fallback.lastError
    ? `连接失败：${fallback.lastError}`
    : "等待可用学生凭据";
}

function vpnPrimaryLabel(status: string) {
  return (
    (
      {
        up: "正常",
        down: "离线",
        degraded: "受限",
        unknown: "待检测",
      } as Record<string, string>
    )[status] || status
  );
}

function dependencyName(name: string) {
  return (
    (
      { mysql: "MySQL", redis: "Redis", minio: "MinIO" } as Record<
        string,
        string
      >
    )[name] || name
  );
}

function jobStatus(status: string) {
  return (
    (
      {
        idle: "等待运行",
        running: "运行中",
        success: "最近成功",
        failed: "最近失败",
      } as Record<string, string>
    )[status] || status
  );
}
